<template>
  <section class="h-full mx-4" id="salesDocumentsView">
    <BaseTable
      v-if="salesDocuments"
      :headers="salesDocumentsHeaders"
      :list="salesDocuments"
      :total-records="salesDocumentsTotalCount"
      :items-per-page="salesDocumentsItemsPerPage"
      :selected-page="salesDocumentsPageNumber"
      table-id="salesDocumentsTable"
      clickable
      @refresh-data="onRefreshData"
      @delete-multiselect="onDeleteMultiselect"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onRowClicked"
      @add-new="onAddNewSalesDocument">
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- First Column -->
          <div class="space-y-4 justify-self-start">
            <div class="space-y-2">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.byStatus')
              }}</label>
              <div
                v-for="status in sortedDocumentStatuses"
                :key="status.id"
                class="flex items-center justify-start gap-4">
                <BaseCheckbox v-model="status.selected" />
                <span class="text-gray-600 text-md">{{ status.name }}</span>
              </div>
            </div>
            <div class="space-y-1">
              <label class="mb-1 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.dateFrom')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringFrom"
                :show-time-picker="false"
                startEmpty
                show-error-as-tooltip
                @validation-change="onDateValidationChange($event, 'filterDateStringFrom')" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.dateTo')
              }}</label>
              <DateTimePicker
                v-model="filterDateStringTo"
                :show-time-picker="false"
                start-empty
                show-error-as-tooltip
                @validation-change="onDateValidationChange($event, 'filterDateStringTo')" />
            </div>
          </div>
          <!-- Second Column -->
          <div class="space-y-4 justify-self-center">
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.bySalesPerson')
              }}</label>
              <BaseSelectDropdown
                name="salesperson"
                :items="dropDownSalesPersons"
                :selected-item="selectedSalesPerson"
                @update:selected="onSalePersonSelected"
                class="w-64" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.byType')
              }}</label>
              <BaseMultiSelectDropdown
                class="w-64 text-sm"
                tags-outside
                place-holder-text="Select category"
                :list-items="dropdownDocumentTypes"
                v-model="filterDocumentTypes" />
            </div>
          </div>
          <!-- Third Column -->
          <div class="space-y-4 justify-self-center">
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.deleted')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.deleted')"
                name="deleted"
                v-model="filterDeleted" />
            </div>
            <!-- Hidden because there is no customer implementation in server yet -->
            <div class="hidden space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.byCustomerGroup')
              }}</label>
              <BaseSelectDropdown
                name="customerGroup"
                :items="dropDownCustomerGroups"
                :selected-item="selectedCustomerGroup"
                clearable
                class="w-64" />
            </div>
            <!-- Hidden because there is no customer implementation in server yet -->
            <div class="hidden space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('salesDocuments.filters.byCustomer')
              }}</label>
              <BaseSelectDropdown
                name="customer"
                :items="dropDownCustomers"
                :selected-item="selectedCustomer"
                clearable
                class="w-64" />
            </div>
          </div>
        </div>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="!item.deleted"
            type="button"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            @click="onDeleteRow(item as SalesDocumentForGrid)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #custom="{ item }: any">
        <div class="text-center">
          <span :class="envClasses({ status: item.documentStatusName.toLowerCase() })">{{
            item.documentStatusName
          }}</span>
        </div>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="confirmDeleteRowModalActive"
      @cancel="confirmDeleteRowModalActive = false"
      @confirm="deleteRowConfirm">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{
          $t('messages.info.deleteSingle', {
            'item': salesDocumentToDelete?.documentNumber,
          })
        }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import { useStateStore } from '@/stores/state';
import {
  getSalesDocumentsWithConditions,
  removeSalesDocumentsByIds,
} from '@/services/api/documents';
import { isoDateToFullDate } from '@/utils/date';
import { useI18n } from 'vue-i18n';
import { cva } from 'class-variance-authority';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseCheckbox from '@/components/common/BaseCheckbox.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type {
  SalesDocument,
  SalesDocumentForGrid,
  SalesDocumentsFiltersData,
} from '@/types/Documents';
import type { SelectDropdownItem } from '@/types/Base';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';

const salesDocuments = ref<SalesDocumentForGrid[]>([]);
const salesDocumentsTotalCount = ref<number>();
const salesDocumentsItemsPerPage = ref(50);
const salesDocumentsPageNumber = ref(1);
const filterIsActive = ref<boolean | undefined>();
const filterDeleted = ref<boolean | undefined>(false);

const { t: $t } = useI18n({ useScope: 'global' });

const { getTableSelectedPage } = useStateStore();

const salesDocumentsHeaders = ref([
  { value: 'officialDate', text: `${$t('date.date')}`, type: 'text', active: true },
  {
    value: 'documentTypeName',
    text: `${$t('salesDocuments.headers.type')}`,
    type: 'text',
    active: true,
  },
  {
    value: 'documentSeriesName',
    text: `${$t('salesDocuments.headers.series')}`,
    type: 'text',
    active: true,
  },
  { value: 'documentNumber', text: 'Number', type: 'text', active: true },
  {
    value: 'traderName',
    text: `${$t('salesDocuments.headers.customer')}`,
    type: 'text',
    active: true,
  },
  {
    value: 'salesPersonName',
    text: `${$t('salesDocuments.headers.salesPerson')}`,
    type: 'text',
    active: true,
  },
  {
    value: 'totalGross',
    text: `${$t('salesDocuments.headers.totalGross')}`,
    type: 'number',
    active: true,
    price: true,
  },
  {
    value: 'totalDiscounts',
    text: `${$t('salesDocuments.headers.totalDiscount')}`,
    type: 'number',
    active: true,
    price: true,
  },
  {
    value: 'totalCharges',
    text: `${$t('salesDocuments.headers.totalCharges')}`,
    type: 'number',
    active: true,
    price: true,
  },
  {
    value: 'documentStatusName',
    text: `${$t('salesDocuments.headers.status')}`,
    type: 'custom',
    active: true,
  },
  { value: 'isFromManager', text: 'From Manager', type: 'check', active: true },

  { value: 'actions', text: 'Actions', active: true },
]);

const envClasses = cva(' text-xs font-normal me-2 px-2.5 py-0.5 rounded-full border ', {
  variants: {
    status: {
      closed: 'text-gray-600 border-gray-400',
      inprogress: 'text-green-600 border-green-400',
      hold: 'text-yellow-600 border-orange-400',
      open: 'text-theme-600 border-theme-400',
    },
  },
  defaultVariants: {
    status: 'open',
  },
});

const { getDocumentStatusesLookup, getDocumentTypesLookup, getSalesPersonsLookup } =
  useLookupsStore();
const { documentStatuses, dropdownDocumentTypes, dropDownSalesPersons } =
  storeToRefs(useLookupsStore());

onMounted(async () => {
  await getDocumentStatusesLookup();
  await getDocumentTypesLookup();
  await getSalesPersonsLookup();

  const storedTableData = getTableSelectedPage('salesDocumentsTable');
  if (storedTableData) {
    salesDocumentsItemsPerPage.value = storedTableData.itemsPerPage;
    salesDocumentsPageNumber.value = storedTableData.page;
  }

  const res = await getSalesDocumentsWithConditions({
    itemsPerPage: salesDocumentsItemsPerPage.value,
    sortColumn: 'createdDate',
    sortAscending: true,
    page: salesDocumentsPageNumber.value,
    deleted: filterDeleted.value,
  });

  if (res) {
    salesDocuments.value = res.data.map((item) => ({
      ...item,
      officialDate: isoDateToFullDate(item.officialDate) || '',
    }));
    salesDocumentsTotalCount.value = res.count;
  }
});

async function onRefreshData(data: SalesDocumentsFiltersData) {
  const sortColumn =
    data.column !== undefined ? salesDocumentsHeaders.value[data.column]?.value : 'officialDate';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || salesDocumentsPageNumber.value,
    itemsPerPage: data.itemsPerPage || salesDocumentsItemsPerPage.value,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    dateFrom: data.dateFrom,
    dateTo: data.dateTo,
    documentStatusIds: data.documentStatusIds,
    documentTypeIds: data.documentTypeIds,
    salesPersonId: data.salesPersonId,
    isActive: data.isActive,
    deleted: data.deleted,
  };

  const res = await getSalesDocumentsWithConditions(params);
  if (res) {
    salesDocuments.value = res.data.map((item) => ({
      ...item,
      officialDate: isoDateToFullDate(item.officialDate) || '',
    }));
    salesDocumentsTotalCount.value = res.count;
  }
}

const toastStore = useToastStore();
const salesDocumentToDelete = ref<SalesDocumentForGrid>();
const confirmDeleteRowModalActive = ref(false);
async function onDeleteRow(item: SalesDocumentForGrid) {
  salesDocumentToDelete.value = item;
  confirmDeleteRowModalActive.value = true;
}

async function deleteRowConfirm() {
  if (!salesDocumentToDelete.value) return;
  const idToDelete = salesDocumentToDelete.value.id;
  const res = await removeSalesDocumentsByIds([idToDelete]);
  if (!res && salesDocuments.value) {
    salesDocuments.value = salesDocuments.value.filter((doc) => doc.id !== idToDelete);
  }
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  confirmDeleteRowModalActive.value = false;
}

async function onDeleteMultiselect(items: SalesDocument[]) {
  const idsToDelete = items.map((item) => item.id);
  const res = await removeSalesDocumentsByIds(idsToDelete);
  if (!res && salesDocuments.value) {
    salesDocuments.value = salesDocuments.value.filter((doc) => !idsToDelete.includes(doc.id));
  }
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const sortedDocumentStatuses = computed(() =>
  documentStatuses.value.slice().sort((a, b) => {
    if (a.status === null && b.status === null) return 0;
    if (a.status === null) return 1;
    if (b.status === null) return -1;
    return a.status - b.status;
  })
);

const filterDateStringFrom = ref<string | undefined>('');
const filterDateStringTo = ref<string | undefined>('');
const dateValidationErrors = ref<Record<string, boolean>>({});

function onDateValidationChange(
  validation: {
    isValid: boolean;
    error: string;
  },
  dateField: 'filterDateStringFrom' | 'filterDateStringTo'
) {
  dateValidationErrors.value[dateField] = !validation.isValid;
}
const filterDocumentTypes = ref<SelectDropdownItem[]>([]);
const selectedSalesPerson = ref<SelectDropdownItem | null>(null);
const dropDownCustomerGroups = ref<SelectDropdownItem[]>([]);
const selectedCustomerGroup = ref<SelectDropdownItem | null>(null);
const dropDownCustomers = ref<SelectDropdownItem[]>([]);
const selectedCustomer = ref<SelectDropdownItem | null>(null);

function onSalePersonSelected(item: SelectDropdownItem) {
  selectedSalesPerson.value = item;
}

async function onApplyFilters(data: SalesDocumentsFiltersData) {
  if (
    dateValidationErrors.value['filterDateStringFrom'] ||
    dateValidationErrors.value['filterDateStringTo']
  ) {
    toastStore.error({ text: $t('messages.error.dateError') });
    return;
  }

  data.dateFrom = filterDateStringFrom.value;
  data.dateTo = filterDateStringTo.value;
  data.isActive = filterIsActive.value;
  data.deleted = filterDeleted.value;
  data.documentStatusIds = documentStatuses.value
    .filter((item) => item.selected === true)
    .map((item) => item.id);
  filterDocumentTypes.value
    ? (data.documentTypeIds = filterDocumentTypes.value
        .map((item) => item.value)
        .filter((value): value is string => typeof value === 'string'))
    : [];
  data.salesPersonId = selectedSalesPerson.value
    ? (selectedSalesPerson.value.value as string)
    : null;
  await onRefreshData(data);
}

function onResetFilters() {
  filterDateStringFrom.value = undefined;
  filterDateStringTo.value = undefined;
  filterDocumentTypes.value = [];
  selectedSalesPerson.value = null;
  filterIsActive.value = undefined;
  filterDeleted.value = false;
  for (const status of documentStatuses.value) {
    status.selected = false;
  }
  salesDocumentsPageNumber.value = 1;
  onRefreshData({
    itemsPerPage: salesDocumentsItemsPerPage.value,
    sortColumn: 'createdDate',
    sortAscending: true,
    page: 1,
    deleted: filterDeleted.value,
  });
}

const router = useRouter();

function onRowClicked(item: SalesDocumentForGrid) {
  router.push({ name: 'sales-documents-details', params: { id: item.id } });
}

const { modifiedSalesDocument } = storeToRefs(useSalesDocumentsStore());
function onAddNewSalesDocument() {
  modifiedSalesDocument.value = undefined;
  router.push({ name: 'sales-documents-details', params: { id: uuidv4() } });
}
</script>
